import axios from 'axios';
import {makeAutoObservable, runInAction} from 'mobx';
import {api} from './api/endpoints';
import {Permission} from './app.model';
import {UsersModel} from './pages/users/users.model';
import {
    BusinessControl,
    ProfileModel,
    AccessGroup,
} from './components/header/settings.model';
import {sizes} from './utils/variables';
import DictionaryStore from "./utils/dictionary/dictionary.store";
import NetworkStore from './network.store';
import {getBrowserTimeZone} from './utils/timezone';
import {BillingSummary} from './pages/billing/subscription/subscription.model';
import {detectMacOS} from './utils/detectMacOS';
import {ENABLE_BILLING} from './config/features';
import {
    consumeBootstrapMode,
    peekBootstrapMode,
    redirectToLandingLogin,
    GREETING_MIN_MS,
    FROM_LOGIN_SESSION_KEY,
    type BootstrapMode,
} from './utils/greeting';
import {normalizeProductPickerModes, setStoredProductPickerMode, PRODUCT_PICKER_CONTEXTS} from './components/ProductPicker';

const BOOTSTRAP_DISPLAY_NAME_KEY = 'visor-bootstrap-display-name';

const readStoredDisplayName = () => localStorage.getItem(BOOTSTRAP_DISPLAY_NAME_KEY);

const persistStoredDisplayName = (name: string | null) => {
    if (name) {
        localStorage.setItem(BOOTSTRAP_DISPLAY_NAME_KEY, name);
        return;
    }
    localStorage.removeItem(BOOTSTRAP_DISPLAY_NAME_KEY);
};

class AppStore {
    public isLoggedIn = !!localStorage.getItem('visor-access-token');
    public isNavOpen = localStorage.getItem('visor-is-nav-open') !== 'false';
    public permissions?: Permission[];
    public userData?: UsersModel;
    public businessSettings?: BusinessControl;
    public needsOnboarding = false;
    public onboardingStatus: string | null = null;
    public billingSummary?: BillingSummary;
    public billingLoading = false;
    public profileUserData?: ProfileModel;
    public profileAccesses?: AccessGroup;
    public devices = {
        mobile: false,
        tablet: false,
        desktop: false,
    };
    public isMacOS = false
    public isBootstrapping = !!localStorage.getItem('visor-access-token');
    public bootstrapMode: BootstrapMode = peekBootstrapMode();
    public bootstrapDisplayName: string | null = readStoredDisplayName();
    private isHandlingUnauthorized = false; // Флаг для предотвращения множественных вызовов
    private isRefreshing = false; // Флаг для отслеживания процесса обновления токена
    private failedQueue: Array<{
        resolve: (value?: any) => void;
        reject: (error?: any) => void;
    }> = []; // Очередь запросов, ожидающих обновления токена

    public simpleTableView: boolean = localStorage.getItem('visor-simple-table-view') !== 'false';

    private initPromise: Promise<void> | null = null;

    constructor() {
        makeAutoObservable(this);
        this.setupAxiosInterceptor();
        this.updateDevices();
        window.addEventListener('resize', this.updateDevices);
    }

    private updateDevices = () => {
        const deviceWidth = window.innerWidth;
        runInAction(() => {
            this.devices.mobile = deviceWidth <= sizes.mobile;
            this.devices.tablet = deviceWidth > sizes.mobile && deviceWidth <= sizes.tablet;
            this.devices.desktop = deviceWidth > sizes.tablet;
        });
    };

    get isAppReady() {
        return Array.isArray(this.permissions) && !this.isBootstrapping;
    }

    public setBootstrapDisplayName = (name: string | null) => {
        const trimmed = name?.trim() || null;
        this.bootstrapDisplayName = trimmed;
        persistStoredDisplayName(trimmed);
    };

    public clearUserDisplayName = () => {
        this.bootstrapDisplayName = null;
        persistStoredDisplayName(null);
    };

    private resetSessionState = () => {
        this.permissions = undefined;
        this.userData = undefined;
        this.businessSettings = undefined;
        this.needsOnboarding = false;
        this.onboardingStatus = null;
        this.billingSummary = undefined;
        this.billingLoading = false;
        this.profileUserData = undefined;
        this.profileAccesses = undefined;
        this.clearUserDisplayName();
        this.isBootstrapping = false;
        // short-catalog session cache must not leak across accounts
        void import('./utils/shortCatalog.cache').then(({clearShortCatalogMemory}) => {
            clearShortCatalogMemory();
        });
    };

    /** Сброс сессии перед новым входом (лендинг → /auth?c=…), чтобы не остаться на предыдущем аккаунте. */
    public clearAuthSession = () => {
        localStorage.removeItem('visor-access-token');
        localStorage.removeItem('visor-refresh-token');
        this.initPromise = null;
        this.isHandlingUnauthorized = false;
        this.isRefreshing = false;
        this.failedQueue = [];
        runInAction(() => {
            this.isLoggedIn = false;
            this.resetSessionState();
        });
    };

    private delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

    private setupAxiosInterceptor = () => {
        // Request interceptor - добавляет токен к каждому запросу
        axios.interceptors.request.use(
            (config) => {
                // Не добавляем Authorization к запросу на refresh
                const isRefreshRequest = config.url?.includes('/refresh');
                if (!isRefreshRequest) {
                    const token = this.getToken();
                    if (token) {
                        config.headers.Authorization = `Bearer ${token}`;
                    }
                }
                const deviceUUID = localStorage.getItem('device_uuid');
                if (deviceUUID) {
                    config.headers['Device-UUID'] = deviceUUID;
                }
                config.headers['X-Time-Zone'] = getBrowserTimeZone();
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor - обрабатывает 401 ошибки и автоматически обновляет токены
        axios.interceptors.response.use(
            (response) => {
                NetworkStore.handleApiSuccess(response.config?.url);
                return response;
            },
            async (error) => {
                NetworkStore.handleApiError(error);
                const originalRequest = error?.config;
                const status = error?.response?.status;
                const url = error?.config?.url || '';
                
                // Проверяем, является ли это запросом к эндпоинту логина или refresh
                const isLoginRequest = url.includes('/login') && !url.includes('/login/');
                const isRefreshRequest = url.includes('/refresh');
                
                // Если это 401 и не логин/refresh запрос, и не на странице логина
                if (status === 401 && !isLoginRequest && !isRefreshRequest && window.location.pathname !== '/auth') {
                    // Если запрос уже был повторен, значит refresh не помог
                    if (originalRequest._retry) {
                        this.handleUnauthorized();
                        return Promise.reject(error);
                    }

                    // Если уже идет процесс обновления токена, добавляем запрос в очередь
                    if (this.isRefreshing) {
                        return new Promise((resolve, reject) => {
                            this.failedQueue.push({ resolve, reject });
                        }).then(() => {
                            // Повторяем оригинальный запрос с новым токеном
                            originalRequest.headers.Authorization = `Bearer ${this.getToken()}`;
                            return axios(originalRequest);
                        }).catch(err => {
                            return Promise.reject(err);
                        });
                    }

                    // Начинаем процесс обновления токена
                    originalRequest._retry = true;
                    this.isRefreshing = true;

                    try {
                        const refreshToken = localStorage.getItem('visor-refresh-token');
                        if (!refreshToken) {
                            throw new Error('No refresh token available');
                        }

                        // Отправляем запрос на обновление токена
                        // Бэкенд ожидает refresh token в cookie "Refresh-Authorization"
                        const deviceUUID = localStorage.getItem('device_uuid') || '';
                        const response = await axios.get(api.refresh, {
                            withCredentials: true, // Оставляем для других cookies, если нужны
                            headers: {
                                'Refresh-Authorization': refreshToken, // Отправляем в заголовке
                                'Device-UUID': deviceUUID
                            }
                        });

                        // Сохраняем новые токены
                        const { access_token, refresh_token } = response.data;
                        if (access_token) {
                            localStorage.setItem('visor-access-token', access_token);
                        }
                        if (refresh_token) {
                            localStorage.setItem('visor-refresh-token', refresh_token);
                        }

                        // Обновляем токен в оригинальном запросе
                        originalRequest.headers.Authorization = `Bearer ${access_token}`;

                        // Обрабатываем очередь ожидающих запросов
                        this.processQueue(null);

                        // Повторяем оригинальный запрос
                        return axios(originalRequest);
                    } catch (refreshError) {
                        // Если refresh не удался, обрабатываем очередь с ошибкой
                        this.processQueue(refreshError);
                        this.handleUnauthorized();
                        return Promise.reject(refreshError);
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(error);
            }
        );
    };

    private processQueue = (error: any) => {
        this.failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve();
            }
        });
        this.failedQueue = [];
    };

    /**
     * Обновляет access token через refresh token. Используется для запросов не через axios (например fetch в чате).
     * @returns новый access_token или null при ошибке (в т.ч. вызовет редирект на логин)
     */
    public refreshAccessToken = async (): Promise<string | null> => {
        if (this.isRefreshing) {
            // Ждём завершения текущего refresh (макс. 10 сек)
            for (let i = 0; i < 50; i++) {
                await new Promise(r => setTimeout(r, 200));
                if (!this.isRefreshing) break;
            }
            return this.getToken();
        }
        const refreshToken = localStorage.getItem('visor-refresh-token');
        if (!refreshToken) {
            this.handleUnauthorized();
            return null;
        }
        this.isRefreshing = true;
        try {
            const deviceUUID = localStorage.getItem('device_uuid') || '';
            const response = await axios.get(api.refresh, {
                withCredentials: true,
                headers: {
                    'Refresh-Authorization': refreshToken,
                    'Device-UUID': deviceUUID
                }
            });
            const { access_token, refresh_token: newRefreshToken } = response.data;
            if (access_token) {
                localStorage.setItem('visor-access-token', access_token);
            }
            if (newRefreshToken) {
                localStorage.setItem('visor-refresh-token', newRefreshToken);
            }
            return access_token ?? this.getToken();
        } catch {
            this.handleUnauthorized();
            return null;
        } finally {
            this.isRefreshing = false;
        }
    };

    private handleUnauthorized = () => {
        // Предотвращаем множественные вызовы
        if (this.isHandlingUnauthorized) {
            return;
        }
        
        // Не редиректим повторно, если уже на странице обмена кода
        if (window.location.pathname === '/auth') {
            return;
        }
        
        this.isHandlingUnauthorized = true;
        
        // Удаляем токены
        localStorage.removeItem('visor-access-token');
        localStorage.removeItem('visor-refresh-token');
        
        // Устанавливаем статус неавторизован
        this.isLoggedIn = false;
        this.resetSessionState();
        
        redirectToLandingLogin();
    };

    public init = async (options?: { force?: boolean }) => {
        if (options?.force) {
            this.initPromise = null;
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this.runInit().finally(() => {
            this.initPromise = null;
        });

        return this.initPromise;
    };

    private runInit = async () => {
        const token = this.getToken();
        if (!token) {
            this.isLoggedIn = false;
            this.isBootstrapping = false;
            return;
        }

        const startedAt = Date.now();
        const bootstrapMode = consumeBootstrapMode();

        runInAction(() => {
            this.bootstrapMode = bootstrapMode;
            this.isBootstrapping = true;
        });

        let loginStatus: number | undefined;

        try {
            const dictionaryStore = DictionaryStore;
            [loginStatus] = await Promise.all([
                this.checkToken(),
                this.getBusinessSettings(),
                dictionaryStore.pageTitleDictionaryReq(),
            ]);

            if (loginStatus === 401) {
                this.handleUnauthorized();
                return;
            }

            const deviceWidth = window.innerWidth;
            runInAction(() => {
                this.devices.mobile = deviceWidth <= sizes.mobile;
                this.devices.tablet = deviceWidth > sizes.mobile && deviceWidth <= sizes.tablet;
                this.devices.desktop = deviceWidth > sizes.tablet;
                this.isMacOS = detectMacOS();
            });

            if (bootstrapMode === 'welcome') {
                const elapsed = Date.now() - startedAt;
                if (elapsed < GREETING_MIN_MS) {
                    await this.delay(GREETING_MIN_MS - elapsed);
                }
            }
        } finally {
            this.isBootstrapping = false;
        }

        if (this.isLoggedIn && Array.isArray(this.permissions)) {
            this.scheduleDeferredBootstrapTasks();
        }
    };

    /** Отложенные запросы после показа shell. FCM — в FirebaseStore.firebasePageInit. */
    private scheduleDeferredBootstrapTasks = () => {
        if (ENABLE_BILLING) {
            void this.getBillingSummary();
        }
    };

    private checkToken = async () => {
        try {
            const token = this.getToken();
            if (!token) {
                return 401;
            }
            const headers = {
                Authorization: 'Bearer ' + token,
            };
            const response = await axios.get(api.get_me, {headers});
            this.setPermissions(response.data.me.access_list);
            this.setUserData(response.data.me.user_data);

            return response.status;
        } catch (err: any) {
            // Fallback для get_me: если interceptor не успел/не сработал, пробуем refresh вручную один раз
            if (err?.response?.status === 401) {
                const newToken = await this.refreshAccessToken();
                if (newToken) {
                    try {
                        const retryRes = await axios.get(api.get_me, {
                            headers: { Authorization: 'Bearer ' + newToken }
                        });
                        this.setPermissions(retryRes.data.me.access_list);
                        this.setUserData(retryRes.data.me.user_data);
                        return retryRes.status;
                    } catch (retryErr: any) {
                        console.error(retryErr);
                        return retryErr?.response?.status ?? 401;
                    }
                }
            }
            console.error(err);
            return err?.response?.status;
        }
    };

    setIsLoggedIn = (status: boolean, options?: { force?: boolean }) => {
        this.isLoggedIn = status;
        if (status && this.getToken()) {
            void this.init({ force: options?.force ?? false });
        }
    };

    /** После входа с лендинга: сразу welcome-режим, затем init() подхватит флаг из sessionStorage. */
    public beginWelcomeBootstrap = () => {
        sessionStorage.setItem(FROM_LOGIN_SESSION_KEY, '1');
        runInAction(() => {
            this.bootstrapMode = 'welcome';
            this.isBootstrapping = true;
        });
    };

    toggleSimpleTableView = () => {
        this.simpleTableView = !this.simpleTableView;
        localStorage.setItem(
            'visor-simple-table-view',
            JSON.stringify(this.simpleTableView)
        );
    };

    toggleIssNavOpen = () => {
        this.isNavOpen = !this.isNavOpen;
        localStorage.setItem('visor-is-nav-open', JSON.stringify(this.isNavOpen));
    };

    public getToken = () => {
        return localStorage.getItem('visor-access-token');
    };

    public setToken = (token: string, tokenKey: string) => {
        localStorage.setItem(tokenKey, token);
    }

    private setPermissions = (permissions: Permission[]) => {
        this.permissions = permissions;
    };

    private setUserData = (userData: UsersModel) => {
        this.userData = userData;
        if (userData.name?.trim()) {
            this.setBootstrapDisplayName(userData.name);
        }
    };

    public getBillingSummary = async () => {
        if (!this.getToken()) {
            this.billingSummary = undefined;
            return;
        }

        this.billingLoading = true;
        try {
            const response = await axios.get(api.billing + '/subscription', {
                headers: {
                    Authorization: 'Bearer ' + this.getToken()!,
                },
            });

            runInAction(() => {
                this.billingSummary = response.data;
            });
        } catch (err: any) {
            console.error(err);
        } finally {
            runInAction(() => {
                this.billingLoading = false;
            });
        }
    };

    public setNeedsOnboarding = (value: boolean, status?: string | null) => {
        this.needsOnboarding = value;
        if (status != null && status !== '') {
            this.onboardingStatus = status;
            return;
        }
        // Не подменяем реальный статус на «completed» при skip/consult без явного status.
        if (!value && (this.onboardingStatus === 'pending' || this.onboardingStatus === 'in_progress')) {
            this.onboardingStatus = null;
        }
    };

    public getBusinessSettings = async () => {
        try {
            const headers = {
                Authorization: 'Bearer ' + this.getToken()!,
            };
            const responseSettings = await axios.get(api.owner, {headers});

            runInAction(() => {
                const owner = responseSettings.data.owner;
                if (owner) {
                    owner.product_picker_modes = normalizeProductPickerModes(
                        owner.product_picker_modes,
                        owner.product_picker_mode,
                    );
                    // Синхронизируем localStorage с настройками компании (для новых — каталог).
                    PRODUCT_PICKER_CONTEXTS.forEach((context) => {
                        const mode = owner.product_picker_modes?.[context];
                        if (mode) {
                            setStoredProductPickerMode(mode, context);
                        }
                    });
                }
                this.businessSettings = owner;
                this.needsOnboarding = Boolean(responseSettings.data.needs_onboarding);
                this.onboardingStatus = responseSettings.data.onboarding_status || null;
            })
        } catch (err: any) {
            console.error(err);
        }
    };

    public getProfile = async () => {
        try {
            const headers = {
                Authorization: 'Bearer ' + this.getToken()!,
            };
            const responseSettings = await axios.get(api.profile, {headers});
            this.profileUserData = responseSettings.data.user_data;
            this.profileAccesses = responseSettings.data.accesses;
        } catch (err: any) {
            console.error(err);
        }
    };


    public getHeaders = () => {
        return {
            'Authorization': 'Bearer ' + this.getToken()!,
            'Device-UUID': localStorage.getItem('device_uuid')
        };
    }
}

export default new AppStore();
